import { alpha } from "@mui/material";
import { makeStyles } from "@mui/styles";
import configData from "../config";
import axios from "axios";

/**
 * getDateFormat
 *
 * @summary Formats a date string into "DD/MM/YYYY" format.
 *
 * @description
 * Converts the provided date string into a JavaScript Date object and returns a string formatted as "day/month/year". Returns an empty string if the input is null.
 *
 * @param {string|null} strdate - The date string to format.
 *
 * @returns {string} The formatted date string in "DD/MM/YYYY" format.
 *
 * @example
 * const formatted = getDateFormat("2024-06-01T12:00:00Z"); // "01/06/2024"
 */
export const getDateFormat = (strdate) => {
  var db_date = "";
  if (strdate !== null) {
    var tdate = new Date(strdate);
    const day = ("0" + tdate.getDate()).slice(-2);
    const month = ("0" + (tdate.getMonth() + 1)).slice(-2);
    db_date = day + "/" + month + "/" + tdate.getFullYear();
  }
  return db_date;
};

/**
 * getDateTimeFormat
 *
 * @summary Formats a date string into "DD/MM/YYYY HH:mm" format.
 *
 * @description
 * Converts the provided date string into a JavaScript Date object and returns a string formatted as "day/month/year hours:minutes". Returns an empty string if the input is null.
 *
 * @param {string|null} strdate - The date string to format.
 *
 * @returns {string} The formatted date and time string in "DD/MM/YYYY HH:mm" format.
 *
 * @example
 * const formatted = getDateTimeFormat("2024-06-01T12:34:00Z"); // "01/06/2024 12:34"
 */
export const getDateTimeFormat = (strdate) => {
  let db_date = "";
  if (strdate !== null) {
    const tdate = new Date(strdate);
    const day = ("0" + tdate.getDate()).slice(-2);
    const month = ("0" + (tdate.getMonth() + 1)).slice(-2);
    const year = tdate.getFullYear();
    const hours = ("0" + tdate.getHours()).slice(-2);
    const minutes = ("0" + tdate.getMinutes()).slice(-2);

    db_date = `${day}/${month}/${year} ${hours}:${minutes}`;
  }
  return db_date;
};

/**
 * getDateFormatUS
 *
 * @summary Formats a date string into "YYYY/MM/DD" format.
 *
 * @description
 * Converts the provided date string into a JavaScript Date object and returns a string formatted as "year/month/day". Returns an empty string if the input is null.
 *
 * @param {string|null} strdate - The date string to format.
 *
 * @returns {string} The formatted date string in "YYYY/MM/DD" format.
 *
 * @example
 * const formatted = getDateFormatUS("2024-06-01T12:00:00Z"); // "2024/06/01"
 */
export const getDateFormatUS = (strdate) => {
  var db_date = "";
  if (strdate !== null) {
    var tdate = new Date(strdate);
    const day = ("0" + tdate.getDate()).slice(-2);
    const month = ("0" + (tdate.getMonth() + 1)).slice(-2);
    db_date = tdate.getFullYear() + "/" + month + "/" + day;
  }
  return db_date;
};

export const AutocompleteX = {
  display: "inline-block",
  width: "100%",
};

export const Mandatory = () => {
  return <span className="mandatoryStar">*</span>;
};

export const waitingTime = () => {
  return 4000;
};

export function errorHandling(error, setError) {
  if (
    error?.response?.status === 400 &&
    typeof error.response.data === "object"
  ) {
    const data = error.response.data;

    if (data.detail) {
      setError(
        <span className="err_color">
          {data.detail}
        </span>
      );
      return;
    }

    const firstError = Object.values(data)?.[0]?.[0];
    if (firstError) {
      setError(
        <span className="err_color">
          {firstError}
        </span>
      );
      return;
    }
  }

  if (
    error?.response?.status === 500 &&
    typeof error.response.data === "object"
  ) {
    const data = error.response.data;

    if (data.detail) {
      setError(
        <span className="err_color">
          {data.detail}
        </span>
      );
      return;
    }

    const firstError = Object.values(data)?.[0]?.[0];
    if (firstError) {
      setError(
        <span className="err_color">
          {firstError}
        </span>
      );
      return;
    }
  }

  if (error?.response) {
    switch (error.response.status) {
      case 401:
        setError(
          <span className="err_color">
            Session expired or unauthorized access. Please log in again.
          </span>
        );
        break;

      case 404:
        setError(
          <span className="err_color">
            Requested resource not found. Please check and try again.
          </span>
        );
        break;

      case 500:
        setError(
          <span className="err_color">
            Server encountered an error. Please contact support or try again later.
          </span>
        );
        break;

      default:
        setError(
          <span className="err_color">
            Something went wrong. Please try again later.
          </span>
        );
        break;
    }
  }
}

export function ButtonStyles(theme) {
  return {
    background: "#129EA3",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "500",
    borderRadius: "8px",
    border: "none",
    alignItems: "center",
    boxShadow: `0 4px 20px ${alpha("#558b9aff", 0.2)}`,
    letterSpacing: "1px",
    cursor: "pointer",
    transition: "transform 0.2s ease-in-out",
    margin: "5px",
    gap: "10px",
    bottom: "5px",
  };
}

export function UploadButton() {
  return {
    background: "#129EA3",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 500,
    borderRadius: "10px",
    boxShadow: `0 4px 20px ${alpha("#558b9aff", 0.2)}`,
    letterSpacing: "1px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    top: "1px",
    bottom: "5px",
    "&:hover": {
      backgroundColor: "#91887e",
    },
  };
}

export function ListItemsStyles(theme) {
  return {
    padding: "0px 10px",
    marginTop: "5px",
    borderRadius: "10px",
    background: "#87cefa",

    [theme.breakpoints.down("sm")]: {
      padding: "0px 6px",
      marginTop: "3px",
      borderRadius: "6px",
      fontSize: "12px",
    },
    [theme.breakpoints.up("md")]: {
      padding: "0px 12px",
      marginTop: "6px",
      borderRadius: "10px",
      fontSize: "14px",
    },
    [theme.breakpoints.up("lg")]: {
      padding: "0px 16px",
      marginTop: "8px",
      borderRadius: "12px",
      fontSize: "15px",
    },
  };
}

export function BackdropStyles() {
  return {
    zIndex: 9999,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  };
}

export function CircularProgressStyles() {
  return {
    zIndex: 10000,
    color: "#314BCA",
  };
}

export function MainReportTableStyles(theme) {
  return {
    border: "1px solid grey",
    borderRadius: "5px",
    backgroundColor: "#fff",
    overflowY: "hidden",
    flexGrow: 1,
    position: "relative",

    maxHeight: "calc(100vh - 220px)",
    overflowY: "auto",

    [theme.breakpoints.down("sm")]: {
      maxHeight: "calc(100vh - 160px)",
      borderRadius: "3px",
    },

    [theme.breakpoints.up("md")]: {
      maxHeight: "calc(100vh - 180px)",
    },

    [theme.breakpoints.up("lg")]: {
      maxHeight: "calc(100vh - 140px)",
    },

    [theme.breakpoints.up("xl")]: {
      maxHeight: "calc(100vh - 100px)",
    },

    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
  };
}

export function ReportTableStyles(theme) {
  return {
    border: "1px solid grey",
    borderRadius: "5px",
    backgroundColor: "#fff",
    flexGrow: 1,
    position: "relative",

    maxHeight: "calc(100vh - 220px)",
    overflowY: "auto",

    scrollbarWidth: "none",
    msOverflowStyle: "none",

    "&::-webkit-scrollbar": {
      display: "none",
    },

    [theme.breakpoints.down("sm")]: {
      maxHeight: "calc(100vh - 160px)",
      borderRadius: "3px",
    },

    [theme.breakpoints.up("md")]: {
      maxHeight: "calc(100vh - 180px)",
    },

    [theme.breakpoints.up("lg")]: {
      maxHeight: "calc(100vh - 140px)",
    },

    [theme.breakpoints.up("xl")]: {
      maxHeight: "calc(100vh - 100px)",
    },
  };
}

export function TableHeaderStyles(theme) {
  return {
    textAlign: "center",
    fontSize: "17px",
    width: "auto",
    fontWeight: "600",
    backgroundColor: "#6eb1f5ff",
    color: "#000",
    border: "1px solid #575757",

    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      padding: "4px",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "15px",
      padding: "6px",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "18px",
      padding: "8px",
    },
  };
}

export function DialogStyles(theme) {
  return {
    "& .MuiDialog-paper": {
      width: "100%",
      borderRadius: "8px",
      backgroundColor: "#fff",

      [theme.breakpoints.down("sm")]: {
        width: "95%",
        borderRadius: "6px",
      },
      [theme.breakpoints.up("md")]: {
        width: "80%",
      },
      [theme.breakpoints.up("lg")]: {
        width: "60%",
      },
    },
  };
}

export const toYearFormat = ((strdate) => {
  var db_date = ''
  if (strdate !== null){
      var tdate = new Date(strdate);
        db_date = tdate.getFullYear()+'-'+(tdate.getMonth()+1)+'-'+tdate.getDate();
  }
  return db_date
});

export const normalizeFilename = (nameOrUrl) => {
  if (!nameOrUrl || typeof nameOrUrl !== "string") return "";

  let name = nameOrUrl.includes("/") ? nameOrUrl.split("/").pop() : nameOrUrl;

  name = name.toLowerCase().trim();

  const parts = name.split(".");
  let ext = parts.length > 1 ? "." + parts.pop().toLowerCase() : "";
  let base = parts.join(".");

  base = base
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9._-]/g, "")
    .replace(/^\.+/, "")

  if (ext && base.endsWith(ext)) {
    base = base.slice(0, -ext.length);
  }

  return base + ext;
};

export function handleApiError(error, setErrorMessage, setErrorDialog, setErrorType) {
  console.error("API Error:", error);

  if (error.code === "ECONNABORTED") {
    return { retry: true };
  }

  if (!error.response) {
    if (!navigator.onLine) {
      setErrorMessage(<span className="err_color">Network connection lost. Please check your internet.</span>);
      setErrorType("network");
    } else {
      setErrorMessage(<span className="err_color">Unable to reach the server at the moment. Please try again shortly or contact the administrator.</span>);
      setErrorType("server");
    }
    setErrorDialog(true);
    return;
  }

  if (error.response.status >= 500) {
    setErrorMessage(<span className="err_color">The server encountered an issue while processing your request. Please try again or contact the administrator.</span>);
    setErrorType("server");
    setErrorDialog(true);
    return;
  }

  setErrorMessage(<span className="err_color">Request failed due to a server issue. Please try again or contact the administrator.</span>);
  setErrorType("server");
  setErrorDialog(true);
}

export const useIconMenuStyles = makeStyles((theme) => ({
  iconMenu: {
    position: "fixed",
    top: "50px",
    left: 0,
    height: "auto",
    width: "220px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    padding: "0px 4px",
    zIndex: 1000,
    overflowY: "auto",
    maxHeight: "calc(100vh - 50px)",
  },

  iconItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    textAlign: "left",
    color: "#FCFCFC",
    textDecoration: "none",
    padding: "5px",
    width: "100%",
    gap: "10px",
    borderRadius: "8px",
    transition: "all 0.3s",
    fontFamily: "IBM Plex Sans, sans-serif",
    "&:hover": {
      backgroundColor: "#87cefa",
      transform: "translateX(4px)",
    },
  },

  iconImage: {
    borderRadius: "5%",
    backgroundSize: "cover",
    backgroundPosition: "center",
    flexShrink: 0,
  },

  "@media (min-width: 1028px)": {
    iconMenu: {
      overflowX: "hidden",
      justifyContent: "flex-start",
    },
    iconItem: {
      width: "100%",
    },
  },
}));

export const useMainLayoutStyles = makeStyles((theme) => ({
    grow: {
        flexGrow: 1
    },
    headerAvatar: {
        ...theme.typography.commonAvatar,
        ...theme.typography.mediumAvatar,
        transition: 'all .2s ease-in-out',
    },
    iconButton: {
        backgroundColor: '#216bfd',
        color: 'white',
        '&:hover': {
            background: theme.palette.secondary.dark,
        },
      },
    disp : {
        bgcolor:'primary.light'
    },
    root: {
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
    },
}));

export const handleEditWithLock = async ({
  module,
  item,
  onSuccess,
  onLocked,
}) => {
  try {
    const res = await axios.post(
      `${configData.API_SERVER}handle-edit-lock/`,
      {
        module,
        record_id: item.jd_id || item.id,
      }
    );

    if (res.data.status === "locked") {
      onLocked?.(res.data);
      return;
    }

    onSuccess(item);

  } catch (err) {
    console.error(err);
    onLocked?.({ message: "Error while locking" });
  }
};

export const releaseLock = async (module, id) => {
  try {
    await axios.post(
      `${configData.API_SERVER}release-edit-lock/`,
      {
        module,
        record_id: id,
      }
    );
  } catch (err) {
    console.error("Release lock failed", err);
  }
};

export const Resizer = ({ onMouseDown }) => (
  <div
    onMouseDown={onMouseDown}
    style={{
      position: "absolute",
      right: 0,
      top: 0,
      height: "100%",
      width: "6px",
      cursor: "col-resize",
      zIndex: 10,
    }}
  />
);